// WooCommerce API Service for Belvoir Farm Shop
// Mirrors duchess service but points at the Farm Shop WordPress install

import OAuth from 'oauth-1.0a';
import CryptoJS from 'crypto-js';

// Determine the correct base URL based on environment
const getBaseURL = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8888/Website/belvoirFarmshop';
    } else if (hostname.includes('staging')) {
      return 'https://staging.belvoircastle.com/api/farm-proxy.php';
    } else {
      return 'https://www.belvoircastle.com/api/farm-proxy.php';
    }
  }
  return 'https://www.belvoircastle.com/api/farm-proxy.php';
};

const BASE_URL = getBaseURL();
const WOOCOMMERCE_API_BASE = BASE_URL.includes('farm-proxy.php') ? BASE_URL : `${BASE_URL}/wp-json/wc/v3`;
const STORE_API_BASE = BASE_URL.includes('farm-proxy.php') ? BASE_URL : `${BASE_URL}/wp-json/wc/store/v1`;

// Debug

// Attempt to parse JSON; if the response is HTML or other content, provide a helpful error
const parseJsonOrReportHtml = async (response, contextLabel = 'Response') => {
  const contentType = response.headers?.get('content-type') || '';
  const bodyText = await response.text();
  if (contentType.includes('application/json')) {
    try { return JSON.parse(bodyText); } catch (e) {}
  }
  try { return JSON.parse(bodyText); } catch (_) {
    const snippet = bodyText.slice(0, 500);
    // eslint-disable-next-line no-console

    throw new SyntaxError(`Non-JSON response (${response.status} ${response.statusText}). Content-Type: ${contentType}.`);
  }
};

// Prefer dedicated Farm Shop credentials via env, fall back to Duchess env if set
const CONSUMER_KEY =
  process.env.REACT_APP_FARM_WOOCOMMERCE_CONSUMER_KEY ||
  process.env.REACT_APP_WOOCOMMERCE_CONSUMER_KEY ||
  '';
const CONSUMER_SECRET =
  process.env.REACT_APP_FARM_WOOCOMMERCE_CONSUMER_SECRET ||
  process.env.REACT_APP_WOOCOMMERCE_CONSUMER_SECRET ||
  '';

if (!CONSUMER_KEY || !CONSUMER_SECRET) {
  // This will still allow fetches that don't need auth (Store API), but classic v3 endpoints will fail
  // Ensure env vars are set in deployment
  // eslint-disable-next-line no-console

}

// OAuth1 configuration for HTTP connections
const oauth = OAuth({
  consumer: {
    key: CONSUMER_KEY,
    secret: CONSUMER_SECRET
  },
  signature_method: 'HMAC-SHA1',
  hash_function(base_string, key) {
    return CryptoJS.HmacSHA1(base_string, key).toString(CryptoJS.enc.Base64);
  }
});

// Helper function to create OAuth1 headers for HTTP connections
const createOAuthHeaders = (url, method = 'GET') => {
  const requestData = { url, method };
  return oauth.toHeader(oauth.authorize(requestData));
};

// Build simple query params
const buildQueryParams = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
};

// =========================
// Store API helper
// =========================
let storeApiNonce = null;
const buildStoreHeaders = () => {
  const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
  const envNonce = process.env.REACT_APP_FARM_WC_STORE_API_NONCE || process.env.REACT_APP_WC_STORE_API_NONCE;
  if (envNonce) headers['X-WC-Store-API-Nonce'] = envNonce;
  return headers;
};

const fetchStoreApi = async (path, { method = 'GET', body = undefined } = {}) => {
  let url, headers;
  
  if (BASE_URL.includes('farm-proxy.php')) {
    // Using proxy - path is just the endpoint like 'cart' or 'checkout'
    const cleanPath = path.replace(/^\//, ''); // Remove leading slash
    
    const proxyParams = new URLSearchParams();
    proxyParams.set('api_type', 'store');
    proxyParams.set('path', cleanPath);
    url = `${BASE_URL}?${proxyParams.toString()}`;
    headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };

  } else {
    // Direct connection - build full URL
    const cleanPath = path.replace(/^\//, '');
    url = `${BASE_URL}/wp-json/wc/store/v1/${cleanPath}`;
    headers = buildStoreHeaders();

  }
  
  const response = await fetch(url, {
    method,
    headers,
    credentials: 'include',
    mode: 'cors',
    body: body !== undefined ? JSON.stringify(body) : undefined
  });
  return response;
};

export const woocommerceFarmService = {
  // Store API (Blocks) helpers (cart-related)
  async getCart() {
    const response = await fetchStoreApi('cart', { method: 'GET' });
    if (!response.ok) {
      throw new Error(`FarmShop: Failed to get cart: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },

  async addCartItem(item) {
    const body = {
      id: item.id,
      quantity: item.quantity || 1,
      variation_id: item.variation_id,
      variation: item.variation
    };
    const response = await fetchStoreApi('cart/add-item', { method: 'POST', body });
    if (!response.ok) {
      let msg = 'Unknown error';
      try { const j = await response.json(); msg = j.message || msg; } catch (_) {}
      throw new Error(`FarmShop: Failed to add cart item: ${msg}`);
    }
    return response.json();
  },

  async clearCart() {

    // Get current cart first
    try {
      const cart = await this.getCart();

      // If cart has no items, nothing to clear
      if (!cart.items || cart.items.length === 0) {

        return cart;
      }
      
      // Delete each item individually
      for (const item of cart.items) {
        try {

          const response = await fetchStoreApi(`cart/items/${item.key}`, { method: 'DELETE' });
          if (!response.ok) {

          }
        } catch (e) {

        }
      }
      
      // Get updated cart
      const updatedCart = await this.getCart();

      return updatedCart;
      
    } catch (error) {

      throw new Error(`FarmShop: Failed to clear cart: ${error.message}`);
    }
  },

  async setCartItems(items = []) {
    await this.clearCart();
    for (const item of items) {
      await this.addCartItem(item);
    }
    return this.getCart();
  },

  // Checkout using WooCommerce Store API with Stripe on-site (Farm Shop)
  async checkoutWithStripe(params) {
    const {
      billing_address,
      shipping_address,
      customer_note = '',
      payment_method = 'stripe',
      payment_data = {}
    } = params || {};

    if (!billing_address) throw new Error('billing_address is required');

    const paymentDataEnhanced = {
      ...payment_data,
      billing_first_name: billing_address?.first_name,
      billing_last_name: billing_address?.last_name,
      billing_email: billing_address?.email,
      email: billing_address?.email,
      customer_email: billing_address?.email,
      payer_email: billing_address?.email,
      billing_phone: billing_address?.phone,
      billing_address_1: billing_address?.address_1,
      billing_address_2: billing_address?.address_2 || '',
      billing_city: billing_address?.city,
      billing_postcode: billing_address?.postcode,
      billing_country: billing_address?.country,
      shipping_first_name: (shipping_address || billing_address)?.first_name,
      shipping_last_name: (shipping_address || billing_address)?.last_name,
      shipping_address_1: (shipping_address || billing_address)?.address_1,
      shipping_address_2: (shipping_address || billing_address)?.address_2 || '',
      shipping_city: (shipping_address || billing_address)?.city,
      shipping_postcode: (shipping_address || billing_address)?.postcode,
      shipping_country: (shipping_address || billing_address)?.country,
    };

    const paymentDataAsArray = Object.entries(paymentDataEnhanced)
      .filter(([key, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => ({ key, value: String(value) }));

    const payload = {
      billing_address,
      shipping_address: shipping_address || billing_address,
      customer_note,
      payment_method,
      payment_data: paymentDataAsArray
    };

    const response = await fetchStoreApi('checkout', { method: 'POST', body: payload });
    if (!response.ok) {
      let msg = 'Unknown error';
      let errJson = null;
      try {
        errJson = await response.json();
        msg = errJson.message || errJson.data?.message || msg;

      } catch (_) {

      }
      throw new Error(`FarmShop checkout failed: ${msg}`);
    }
    const checkoutResult = await response.json();

    return checkoutResult;
  },

  extractStripeClientSecret(response) {
    if (!response) {

      return null;
    }

    // Store API checkout response paths
    const cs1 = response?.payment_result?.payment_details?.client_secret;
    if (cs1) {

      return cs1;
    }
    
    const cs2 = response?.payment_result?.client_secret;
    if (cs2) {

      return cs2;
    }
    
    const cs3 = response?.client_secret;
    if (cs3) {

      return cs3;
    }
    
    const cs4 = response?.payment_result?.payment_details?.stripe_client_secret;
    if (cs4) {

      return cs4;
    }
    
    // Check order metadata (for REST API created orders)
    if (response.meta_data && Array.isArray(response.meta_data)) {
      const clientSecretMeta = response.meta_data.find(m => 
        m.key === 'stripe_client_secret' || 
        m.key === '_stripe_client_secret' ||
        m.key === 'client_secret'
      );
      
      if (clientSecretMeta && clientSecretMeta.value) {

        return clientSecretMeta.value;
      }
      
      // Check for payment intent ID and construct client secret if needed
      const intentIdMeta = response.meta_data.find(m => 
        m.key === '_stripe_intent_id' || m.key === 'stripe_intent_id'
      );
      
      if (intentIdMeta && intentIdMeta.value) {

        // We have the intent ID but not the client secret
        // We'd need to call Stripe API to get the client secret
      }
    }

    return null;
  },

  async getPaymentMethods() {
    // Try Store API endpoints first
    const storeEndpoints = ['payment-methods', 'payments/methods'];
    for (const path of storeEndpoints) {
      try {
        const response = await fetchStoreApi(path, { method: 'GET' });
        if (response.ok) return response.json();
      } catch (_) {}
    }
    
    // Fallback to v3 API
    try {
      let url, headers;
      if (BASE_URL.includes('farm-proxy.php')) {
        const proxyParams = new URLSearchParams();
        proxyParams.set('path', 'payment_gateways');
        url = `${WOOCOMMERCE_API_BASE}?${proxyParams.toString()}`;
        headers = { 'Content-Type': 'application/json' };
      } else {
        url = `${WOOCOMMERCE_API_BASE}/payment_gateways`;
        headers = { 'Content-Type': 'application/json', ...createOAuthHeaders(url, 'GET') };
      }
      
      const response = await fetch(url, { 
        method: 'GET', 
        headers,
        mode: 'cors'
      });
      if (response.ok) return response.json();
    } catch (_) {}
    
    throw new Error('FarmShop: Failed to fetch payment methods');
  },

  async debugStoreApi() {
    try {
      await this.getCart();
    } catch (_) {}
    try {
      await this.getPaymentMethods();
    } catch (_) {}
  },

  // Core catalog endpoints (v3)
  async getProducts(options = {}) {
    const {
      page = 1,
      per_page = 12,
      category = '',
      search = '',
      status = 'publish',
      featured = false,
      on_sale = false,
      orderby = 'date',
      order = 'desc',
      stock_status = 'instock'
    } = options;

    const params = { page, per_page, status, orderby, order };
    if (category) params.category = category;
    if (search) params.search = search;
    if (featured) params.featured = true;
    if (on_sale) params.on_sale = true;
    if (stock_status) params.stock_status = stock_status;

    const queryString = buildQueryParams(params);
    let url, headers;
    if (BASE_URL.includes('farm-proxy.php')) {
      const proxyParams = new URLSearchParams();
      proxyParams.set('path', 'products');
      if (queryString) {
        const original = new URLSearchParams(queryString);
        original.forEach((v, k) => proxyParams.set(k, v));
      }
      url = `${WOOCOMMERCE_API_BASE}?${proxyParams.toString()}`;
      headers = {};
    } else {
      url = `${WOOCOMMERCE_API_BASE}/products${queryString ? `?${queryString}` : ''}`;
      headers = { 'Content-Type': 'application/json', ...createOAuthHeaders(url, 'GET') };
    }

    const response = await fetch(url, { method: 'GET', headers, mode: 'cors' });
    if (!response.ok) {
      let errorData = {};
      try { errorData = await parseJsonOrReportHtml(response.clone(), 'Farm Products (error)'); } catch (e) { errorData = { message: e.message }; }
      throw new Error(`FarmShop: Failed to fetch products: ${response.status} ${response.statusText} - ${errorData.message || 'Unknown error'}`);
    }
    // Extract pagination info from response headers BEFORE cloning
    const total = parseInt(response.headers.get('X-WP-Total') || '0', 10);
    const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '1', 10);
    
    const products = await parseJsonOrReportHtml(response.clone(), 'Farm Products');
    if (!Array.isArray(products)) {
      const message = typeof products?.message === 'string' ? products.message : 'Unexpected response shape (expected array)';
      // eslint-disable-next-line no-console

      throw new Error(message);
    }
    
    return {
      products,
      total,
      totalPages
    };
  },

  async getProduct(productId) {
    let url, headers;
    if (BASE_URL.includes('farm-proxy.php')) {
      const proxyParams = new URLSearchParams();
      proxyParams.set('path', `products/${productId}`);
      url = `${WOOCOMMERCE_API_BASE}?${proxyParams.toString()}`;
      headers = { 'Content-Type': 'application/json' };
    } else {
      url = `${WOOCOMMERCE_API_BASE}/products/${productId}`;
      headers = { 'Content-Type': 'application/json', ...createOAuthHeaders(url, 'GET') };
    }
    
    const response = await fetch(url, { method: 'GET', headers, mode: 'cors' });
    if (!response.ok) {
      let errorData = {};
      try { errorData = await parseJsonOrReportHtml(response.clone(), 'Farm Product (error)'); } catch (e) { errorData = { message: e.message }; }
      throw new Error(`FarmShop: Failed to fetch product: ${response.status} ${response.statusText} - ${errorData.message || 'Unknown error'}`);
    }
    return parseJsonOrReportHtml(response.clone(), 'Farm Product');
  },

  async getCategories(options = {}) {
    const { page = 1, per_page = 100, parent = 0, orderby = 'name', order = 'asc' } = options;
    const params = { page, per_page, parent, orderby, order };
    const queryString = buildQueryParams(params);
    let url, headers;
    if (BASE_URL.includes('farm-proxy.php')) {
      const proxyParams = new URLSearchParams();
      proxyParams.set('path', 'products/categories');
      if (queryString) {
        const original = new URLSearchParams(queryString);
        original.forEach((v, k) => proxyParams.set(k, v));
      }
      url = `${WOOCOMMERCE_API_BASE}?${proxyParams.toString()}`;
      headers = {};
    } else {
      url = `${WOOCOMMERCE_API_BASE}/products/categories${queryString ? `?${queryString}` : ''}`;
      headers = { 'Content-Type': 'application/json', ...createOAuthHeaders(url, 'GET') };
    }
    const response = await fetch(url, { method: 'GET', headers, mode: 'cors' });
    if (!response.ok) {
      let errorData = {};
      try { errorData = await parseJsonOrReportHtml(response.clone(), 'Farm Categories (error)'); } catch (e) { errorData = { message: e.message }; }
      throw new Error(`FarmShop: Failed to fetch categories: ${response.status} ${response.statusText} - ${errorData.message || 'Unknown error'}`);
    }
    const categories = await parseJsonOrReportHtml(response.clone(), 'Farm Categories');
    if (!Array.isArray(categories)) {
      const message = typeof categories?.message === 'string' ? categories.message : 'Unexpected response shape (expected array)';
      // eslint-disable-next-line no-console

      throw new Error(message);
    }
    return categories;
  },

  async getFeaturedProducts(options = {}) {
    return this.getProducts({ ...options, featured: true });
  },

  async createOrder(orderData) {
    let url, headers;
    if (BASE_URL.includes('farm-proxy.php')) {
      const proxyParams = new URLSearchParams();
      proxyParams.set('path', 'orders');
      url = `${WOOCOMMERCE_API_BASE}?${proxyParams.toString()}`;
      headers = { 'Content-Type': 'application/json' };
    } else {
      url = `${WOOCOMMERCE_API_BASE}/orders`;
      headers = { 'Content-Type': 'application/json', ...createOAuthHeaders(url, 'POST') };
    }
    
    const response = await fetch(url, { 
      method: 'POST', 
      headers, 
      body: JSON.stringify(orderData),
      mode: 'cors'
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      throw new Error(`FarmShop: Failed to create order: ${response.status} ${response.statusText} - ${errorData.message || 'Unknown error'}`);
    }
    return response.json();
  },
  
  async createOrderWithStripe(orderData) {

    // First create the order
    const order = await this.createOrder(orderData);

    // The order should have Stripe metadata/client secret
    // Check if WooCommerce Stripe already created a payment intent
    if (order.meta_data) {
      const stripeIntentMeta = order.meta_data.find(m => 
        m.key === '_stripe_intent_id' || 
        m.key === '_stripe_source_id' ||
        m.key === 'stripe_client_secret'
      );
      
      if (stripeIntentMeta) {

      }
    }
    
    // If no client secret in order, we need to create a payment intent via Stripe
    // For now, return the order and let the caller handle Stripe initialization
    return order;
  },
  
  async updateOrderStatus(orderId, data) {
    let url, headers;
    if (BASE_URL.includes('farm-proxy.php')) {
      const proxyParams = new URLSearchParams();
      proxyParams.set('path', `orders/${orderId}`);
      url = `${WOOCOMMERCE_API_BASE}?${proxyParams.toString()}`;
      headers = { 'Content-Type': 'application/json' };
    } else {
      url = `${WOOCOMMERCE_API_BASE}/orders/${orderId}`;
      headers = { 'Content-Type': 'application/json', ...createOAuthHeaders(url, 'PUT') };
    }
    
    const response = await fetch(url, { 
      method: 'PUT', 
      headers, 
      body: JSON.stringify(data),
      mode: 'cors'
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      throw new Error(`FarmShop: Failed to update order: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },

  // Validate a coupon code
  async validateCoupon(couponCode) {
    if (!couponCode || !couponCode.trim()) {
      throw new Error('Coupon code is required');
    }

    let url, headers;
    if (BASE_URL.includes('farm-proxy.php')) {
      const proxyParams = new URLSearchParams();
      proxyParams.set('path', `coupons/${encodeURIComponent(couponCode.trim())}`);
      url = `${WOOCOMMERCE_API_BASE}?${proxyParams.toString()}`;
      headers = { 'Content-Type': 'application/json' };
    } else {
      url = `${WOOCOMMERCE_API_BASE}/coupons/${encodeURIComponent(couponCode.trim())}`;
      headers = { 'Content-Type': 'application/json', ...createOAuthHeaders(url, 'GET') };
    }
    
    const response = await fetch(url, { 
      method: 'GET', 
      headers,
      mode: 'cors'
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData?.message || 'Invalid coupon code';
      throw new Error(errorMessage);
    }
    
    const coupon = await response.json();
    
    // Check if coupon is valid and active
    if (coupon.status !== 'publish') {
      throw new Error('This coupon code is not available');
    }
    
    // Check usage limits
    if (coupon.usage_limit && coupon.usage_count >= coupon.usage_limit) {
      throw new Error('This coupon has reached its usage limit');
    }
    
    // Check if coupon has expired
    if (coupon.date_expires) {
      const expiryDate = new Date(coupon.date_expires);
      if (expiryDate < new Date()) {
        throw new Error('This coupon has expired');
      }
    }
    
    return coupon;
  }
};

export default woocommerceFarmService;

